/**
 * Authentication provider.
 *
 * Owns the sign-in lifecycle described in §9:
 *   1. Firebase authenticates.
 *   2. RevenueCat is configured with the Firebase UID as App User ID.
 *   3. The MongoDB profile is synced via POST /auth/sync.
 *   4. Entitlements are read back from the API (which is authoritative).
 *
 * On sign-out every private query cache entry is cleared so the next person to
 * use the browser cannot see the previous user's library.
 */

import { createContext, useContext, useEffect, useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { clearPrivateQueries } from '@chordbookpro/api-client';
import { useAuthStore } from '../stores/authStore.js';
import { useUiStore } from '../stores/uiStore.js';
import { api, setUnauthenticatedHandler } from '../services/api.js';
import * as authService from '../services/authService.js';
import * as subscriptionService from '../services/subscriptions/subscriptionService.js';
import * as subscriptionCache from '../services/subscriptions/subscriptionCache.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const showToast = useUiStore((state) => state.showToast);

  const {
    firebaseUser,
    profile,
    initialized,
    busy,
    pendingLinkCredential,
    setFirebaseUser,
    setProfile,
    setInitialized,
    setBusy,
    setPendingLinkCredential,
    reset,
  } = useAuthStore();

  // Guards against two sync calls racing when auth state settles.
  const syncingRef = useRef(false);

  /**
   * Set when POST /auth/sync fails.
   *
   * This is a whole-account failure, not a single broken request: without a
   * server-side profile, favourites, history and settings all fail too. It is
   * surfaced as a persistent, retryable banner.
   */
  const [syncError, setSyncError] = useState(null);
  const [retryingSync, setRetryingSync] = useState(false);

  /** Runs the post-authentication sequence. */
  const completeSignIn = useCallback(
    async (user) => {
      if (!user || syncingRef.current) return;
      syncingRef.current = true;

      try {
        // RevenueCat is keyed by the Firebase UID on every platform, so the
        // same person has one customer record across web, iOS and Android.
        await subscriptionService.initializeForUser(user.uid);

        // If the user arrived via a provider whose email already belonged to
        // another sign-in method, attach it now that they are authenticated.
        if (pendingLinkCredential) {
          await authService.linkPendingCredential(pendingLinkCredential).catch(() => {});
          setPendingLinkCredential(null);
        }

        const result = await api.auth.sync({
          displayName: user.displayName ?? undefined,
          photoURL: user.photoURL ?? undefined,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          locale: navigator.language,
        });

        setProfile(result.profile);
        setSyncError(null);

        // Seed the caches the app reads immediately after sign-in.
        queryClient.setQueryData(['auth', 'me'], result);
        queryClient.setQueryData(['billing', 'status'], {
          ...result.subscription,
          limits: result.limits,
          usage: result.usage,
          remaining: result.remaining,
        });
      } catch (error) {
        console.error('Post-sign-in sync failed:', error);

        // A transient toast is the wrong shape for this: when /auth/sync fails
        // the account has no server-side profile at all, so favourites, history
        // and settings are ALL broken until it succeeds. That warrants a
        // persistent banner with a retry, not a message that disappears.
        setSyncError(error);
      } finally {
        syncingRef.current = false;
      }
    },
    [pendingLinkCredential, queryClient, setPendingLinkCredential, setProfile, showToast],
  );

  /* Subscribe to Firebase auth state. */
  useEffect(() => {
    const unsubscribe = authService.subscribeToAuthState(async (user) => {
      setFirebaseUser(user);

      if (user) {
        await completeSignIn(user);
      } else {
        setProfile(null);
      }

      setInitialized(true);
    });

    return unsubscribe;
    // completeSignIn is intentionally omitted: re-subscribing on every render
    // of that callback would tear down and rebuild the Firebase listener.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFirebaseUser, setInitialized, setProfile]);

  /** Signs out everywhere and clears private state. */
  const signOut = useCallback(async () => {
    await authService.signOut();
    await subscriptionService.resetForSignOut();

    // Remove every user-scoped cache entry before the next sign-in.
    clearPrivateQueries(queryClient);
    subscriptionCache.clearSubscription();
    reset();
  }, [queryClient, reset]);

  /* An invalid session mid-use should return the user to a signed-out state
     cleanly rather than leaving the app in a half-authenticated limbo. */
  useEffect(() => {
    setUnauthenticatedHandler((error) => {
      if (error?.code === 'TOKEN_EXPIRED' || error?.code === 'INVALID_TOKEN') {
        signOut().catch(() => {});
        showToast({ message: 'Your session ended. Please sign in again.', tone: 'warning' });
      }
    });

    return () => setUnauthenticatedHandler(null);
  }, [signOut, showToast]);

  const value = {
    firebaseUser,
    profile,
    initialized,
    busy,
    isAuthenticated: Boolean(firebaseUser),
    isEmailVerified: Boolean(firebaseUser?.emailVerified),
    role: profile?.role ?? 'user',
    isAdmin: profile?.role === 'admin',
    isModerator: profile?.role === 'admin' || profile?.role === 'moderator',

    /**
     * True when Firebase authenticated the user but the server-side profile is
     * missing. Nothing account-scoped works in this state, so the UI surfaces
     * it rather than rendering empty fields and dead buttons.
     */
    profileMissing: Boolean(firebaseUser) && !profile,
    syncError,
    retryingSync,

    async retrySync() {
      setRetryingSync(true);
      try {
        await completeSignIn(firebaseUser);
      } finally {
        setRetryingSync(false);
      }
    },

    async register(values) {
      setBusy(true);
      try {
        return await authService.registerWithEmail(values);
      } finally {
        setBusy(false);
      }
    },

    async signIn(values) {
      setBusy(true);
      try {
        return await authService.signInWithEmail(values);
      } finally {
        setBusy(false);
      }
    },

    async signInWithGoogle() {
      setBusy(true);
      try {
        return await authService.signInWithGoogle();
      } catch (error) {
        // Stash the credential so it can be linked after the user signs in
        // with the provider they already have.
        if (error?.details?.pendingCredential) {
          setPendingLinkCredential(error.details.pendingCredential);
        }
        throw error;
      } finally {
        setBusy(false);
      }
    },

    async signInWithApple() {
      setBusy(true);
      try {
        return await authService.signInWithApple();
      } catch (error) {
        if (error?.details?.pendingCredential) {
          setPendingLinkCredential(error.details.pendingCredential);
        }
        throw error;
      } finally {
        setBusy(false);
      }
    },

    signOut,
    resendVerificationEmail: authService.resendVerificationEmail,
    requestPasswordReset: authService.requestPasswordReset,
    completePasswordReset: authService.completePasswordReset,
    changePassword: authService.changePassword,
    reloadUser: authService.reloadUser,
    refreshProfile: () => completeSignIn(firebaseUser),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
